import React, { useEffect, useRef, useState } from 'react';
import { paintCameraOverlay, pictureFit } from '../../../util/cameraReprojection';
import { frameFromMediaTime } from '../../../util/trialLiveLayout';

function presentedMediaTime(video, metadata) {
  if (metadata && Number.isFinite(metadata.mediaTime)) {
    // A callback queued across pause can report a stale mediaTime of 0.
    if (metadata.mediaTime === 0) {
      const current = video.currentTime;
      if (Number.isFinite(current) && current > 0.25) return current;
    }
    return metadata.mediaTime;
  }
  const current = video.currentTime;
  return Number.isFinite(current) ? current : 0;
}

function readObjectFit(video) {
  let fit = '';
  try {
    fit = window.getComputedStyle(video).objectFit || '';
  } catch (err) {
    fit = '';
  }
  if (fit === 'contain' || fit === 'cover' || fit === 'fill') return fit;
  return 'contain';
}

function setBoxStyle(canvas, name, value) {
  if (canvas.style[name] !== value) canvas.style[name] = value;
}

/** Canvas rectangle for the painted picture, excluding the player's black bars. */
function measurePicture(video, canvas, track) {
  const box = video.getBoundingClientRect();
  const parent = video.parentElement;
  const parentBox = parent ? parent.getBoundingClientRect() : box;
  const mediaW = video.videoWidth || track.width;
  const mediaH = video.videoHeight || track.height;
  const placed = pictureFit(box.width, box.height, mediaW, mediaH, readObjectFit(video));
  if (!placed) return null;
  const dpr = window.devicePixelRatio || 1;
  setBoxStyle(canvas, 'left', `${(box.left - parentBox.left) + placed.offsetX}px`);
  setBoxStyle(canvas, 'top', `${(box.top - parentBox.top) + placed.offsetY}px`);
  setBoxStyle(canvas, 'width', `${placed.displayedWidth}px`);
  setBoxStyle(canvas, 'height', `${placed.displayedHeight}px`);
  const bufferW = Math.max(1, Math.round(placed.displayedWidth * dpr));
  const bufferH = Math.max(1, Math.round(placed.displayedHeight * dpr));
  if (canvas.width !== bufferW) canvas.width = bufferW;
  if (canvas.height !== bufferH) canvas.height = bufferH;
  return {
    dpr,
    boxWidth: placed.displayedWidth,
    boxHeight: placed.displayedHeight,
    imageWidth: mediaW,
    imageHeight: mediaH,
    ctx: canvas.getContext('2d', { alpha: true, desynchronized: true }),
  };
}

function watchCameraFrames(video, onFrame) {
  let stopped = false;
  let handle = 0;
  const supports = typeof video.requestVideoFrameCallback === 'function';
  if (supports) {
    const onVideoFrame = (_now, metadata) => {
      if (stopped) return;
      onFrame(presentedMediaTime(video, metadata));
      if (!stopped) handle = video.requestVideoFrameCallback(onVideoFrame);
    };
    handle = video.requestVideoFrameCallback(onVideoFrame);
    return {
      coarse: false,
      stop() {
        stopped = true;
        if (typeof video.cancelVideoFrameCallback === 'function') {
          video.cancelVideoFrameCallback(handle);
        }
      },
    };
  }
  const onTime = () => onFrame(Number.isFinite(video.currentTime) ? video.currentTime : 0);
  video.addEventListener('timeupdate', onTime);
  video.addEventListener('seeked', onTime);
  onTime();
  return {
    coarse: true,
    stop() {
      stopped = true;
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('seeked', onTime);
    },
  };
}

function CameraBoneOverlay({ videoRef, track, joints, edges }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const [coarse, setCoarse] = useState(false);

  useEffect(() => {
    const video = videoRef && videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return undefined;
    let lastTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
    let lastFrame = -1;
    let geometry = track ? measurePicture(video, canvas, track) : null;
    const draw = (mediaTime) => {
      lastTime = mediaTime;
      const fps = track && Number(track.fps) > 0 ? track.fps : 30;
      const count = track && Array.isArray(track.xy) && track.xy.length > 0
        ? track.xy.length
        : 1e15;
      const frame = frameFromMediaTime(mediaTime, fps, count);
      const label = String(frame);
      if (frameRef.current && frameRef.current.textContent !== label) {
        frameRef.current.textContent = label;
      }
      if (video.dataset.presentedFrame !== label) {
        video.dataset.presentedFrame = label;
        video.dispatchEvent(new CustomEvent('presentedframe'));
      }
      if (!track || !geometry || !geometry.ctx || frame === lastFrame) return;
      lastFrame = frame;
      paintCameraOverlay(geometry.ctx, {
        boxWidth: geometry.boxWidth,
        boxHeight: geometry.boxHeight,
        imageWidth: geometry.imageWidth,
        imageHeight: geometry.imageHeight,
        framePoints: track.xy[frame],
        edges,
        joints,
        dpr: geometry.dpr,
        fit: 'fill',
      });
    };
    const refit = () => {
      lastFrame = -1;
      geometry = track ? measurePicture(video, canvas, track) : null;
      draw(lastTime);
    };
    const watch = watchCameraFrames(video, draw);
    setCoarse(watch.coarse);
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(refit);
      observer.observe(video);
    }
    video.addEventListener('loadedmetadata', refit);
    return () => {
      watch.stop();
      if (observer) observer.disconnect();
      video.removeEventListener('loadedmetadata', refit);
    };
  }, [videoRef, track, joints, edges]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="trial-live-cam-overlay"
        data-testid="trial-live-cam-overlay"
        aria-hidden="true"
      />
      <span
        ref={frameRef}
        className="trial-live-cam-frame"
        data-testid="trial-live-cam-frame"
      >
        0
      </span>
      {coarse ? (
        <span
          className="trial-live-cam-coarse"
          data-testid="trial-live-overlay-coarse"
          title="This browser has no video frame callback, so the skeleton follows playback time instead of each frame."
        >
          Coarse overlay
        </span>
      ) : null}
    </>
  );
}

export default CameraBoneOverlay;
